import type { CityConfig } from "@/pages/city-template/types";

export const janesvilleConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Janesville",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "janesville",
  route: "/cities/janesville",
  title: "Emergency & Expedited Passport Services Janesville WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Janesville, WI — as fast as 24 hours. No Wisconsin passport agency. Fast Passport Center couriers to Chicago, about 105 miles southeast via I-90. A+ BBB rated. Serving Rock County, Beloit, Whitewater, and south central Wisconsin.",
  h1: "Emergency & Expedited Passport Services in Janesville, WI",
  heroSubtitle: "Janesville anchors Wisconsin's southern border along the I-90 corridor, just 105 miles from the Chicago Passport Agency. Fast Passport Center's registered couriers deliver your documents to Chicago so you get a new or renewed passport as fast as 24 hours — whether you're in downtown Janesville, Beloit, Whitewater, or anywhere in Rock County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Janesville%20Wisconsin%20Rock%20River%20flowing%20through%20historic%20downtown%20with%20vintage%20limestone%20buildings%20and%20arched%20bridges%20at%20golden%20hour%2C%20warm%20amber%20tones%20reflecting%20on%20calm%20river%20water%2C%20classic%20midwestern%20cityscape%20with%20industrial%20heritage%2C%20professional%20landscape%20photography%2C%20wide%20angle%20composition%20showing%20river%20corridor%20and%20urban%20architecture%2C%20soft%20natural%20light%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=janesville-hero-001&orientation=landscape",
  heroImageAlt: "Janesville Wisconsin Rock River waterfront and historic downtown at golden hour",
  geoPosition: "42.6828;-89.0187",
  geoPlacename: "Janesville, Wisconsin",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 2500, Chicago, IL 60604",
    distance: "Approximately 105 miles from Janesville — about 1 hour 45 minutes via I-90 southeast",
  },
  nearbyCitiesZips: [
    { name: "Downtown Janesville", zips: "53545, 53546" },
    { name: "East Janesville", zips: "53548" },
    { name: "Beloit", zips: "53511, 53512" },
    { name: "Whitewater", zips: "53190" },
    { name: "Milton", zips: "53563" },
    { name: "Edgerton", zips: "53534" },
    { name: "Evansville", zips: "53536" },
    { name: "Delavan", zips: "53115" },
    { name: "Elkhorn", zips: "53121" },
    { name: "Fort Atkinson", zips: "53538" },
    { name: "Stoughton", zips: "53589" },
    { name: "Clinton", zips: "53525" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Chicago Passport Agency — 105 miles on the I-90 corridor" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Rock County Hub", desc: "Serving Janesville, Beloit, Whitewater, and all of south central Wisconsin" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Janesville case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Madison WI", route: "/cities/madison-wi" },
    { name: "Expedited passport in Milwaukee WI", route: "/cities/milwaukee" },
    { name: "Urgent passport in Racine WI", route: "/cities/racine" },
    { name: "Wisconsin passport hub", route: "/states/wisconsin" },
    { name: "Chicago passport services", route: "/states/illinois" },
    { name: "Emergency passport in Beloit WI", route: "/cities/beloit" },
    { name: "Expedited passport in Rockford IL", route: "/cities/rockford" },
  ],
  fpcOfficeAddress: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
  fpcOfficeNote: "Fast Passport Center serves Janesville remotely via secure courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency. Janesville's position on the I-90 corridor — just 105 miles from Chicago — provides fast and reliable courier turnaround for all of Rock County.",
  fpcOfficeDistance: "Serving all of Rock County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Janesville, Beloit, Whitewater, and south central Wisconsin. From downtown Janesville along the Rock River to the Wisconsin-Illinois border communities — we help Rock County travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Janesville and nearby communities including Beloit, Whitewater, Milton, Edgerton, Evansville, Delavan, Fort Atkinson, and the entire Rock County region.",
  howItWorksIntro: "Most Janesville travelers complete everything online and ship documents overnight. At about 105 miles from Chicago on the I-90 corridor, Janesville enjoys fast courier turnaround — ideal for Rock County families and southern Wisconsin professionals.",
  mistakesIntro: "Janesville travelers, including cross-border commuters to Illinois and Rock County families, have unique passport needs. Here are the most common mistakes we see.",
  testimonialsIntro: "Janesville and Rock County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Janesville? Here are the most common ones we hear from Rock County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 105 miles to Chicago (1 hr 45 min via I-90)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Direct I-90 corridor to Chicago",
        "No drive or appointment needed",
        "Ideal for Rock County families",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from MSN, MKE, or RFD",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Janesville is about 105 miles from the Chicago Passport Agency via I-90. Our couriers achieve same-day delivery for 24-hour turnaround.",
      bullets: ["Direct I-90 corridor to Chicago", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in WI",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Janesville adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Janesville and Rock County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Janesville and Rock County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Janesville, Beloit, and Rock County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago Agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Rock County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Janesville and Beloit residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Janesville WI", description: "Urgent passport processing for Janesville, WI travelers via Chicago Passport Agency courier — about 105 miles from Janesville on the I-90 corridor." },
    { name: "Passport Renewal Janesville WI", description: "Fast passport renewal for Janesville and Rock County, Wisconsin residents." },
    { name: "New Passport Janesville WI", description: "First-time passport application assistance for Janesville and south central Wisconsin residents." },
    { name: "Lost Passport Janesville WI", description: "Passport replacement for lost or stolen passports for Janesville and Beloit residents." },
    { name: "Child Passport Janesville WI", description: "Minor passport services for Janesville and Rock County families." },
    { name: "Name Change Passport Janesville WI", description: "Passport name change service for Janesville, WI residents." },
  ],
  testimonials: [
    {
      name: "David R.",
      location: "East Janesville, WI",
      rating: 5,
      text: "Planned a family vacation to Cancun and discovered my daughter's passport expired. Fast Passport Center processed her renewal in 4 business days. The whole family made the flight. Couldn't ask for better service.",
      service: "Child Passport",
    },
    {
      name: "Patricia M.",
      location: "Beloit, WI",
      rating: 5,
      text: "Living on the Wisconsin-Illinois border, I commuted to Rockford for work. Needed an urgent passport for a company trip to Toronto. Fast Passport Center got my renewal done in 3 days. Fantastic service for the Beloit-Janesville area.",
      service: "Expedited Renewal",
    },
    {
      name: "Michael W.",
      location: "Milton, WI",
      rating: 5,
      text: "First time getting a passport and I had no idea what I was doing. Fast Passport Center walked me through every form, found me an acceptance facility in Janesville, and got me my passport in 6 business days.",
      service: "New Passport",
    },
    {
      name: "Karen S.",
      location: "Downtown Janesville",
      rating: 5,
      text: "Lost my passport somewhere between O'Hare and Janesville. Had another international trip in 8 days. Fast Passport Center helped with the DS-64 and got the replacement to me in 3 business days. Absolutely incredible.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "jan-1",
      name: "Janesville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1818 Milton Ave",
      city: "Janesville",
      state: "WI",
      zip: "53545",
      phone: "(608) 754-6700",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Central Janesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Janesville. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "jan-2",
      name: "Rock County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "51 S Main St",
      city: "Janesville",
      state: "WI",
      zip: "53545",
      phone: "(608) 757-5560",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Janesville",
      website: "https://www.co.rock.wi.us/county-clerk",
      websiteLabel: "Rock County Clerk",
      notes: "Accepts passport applications for Rock County residents. Located in the Rock County Courthouse.",
      icon: "ri-government-line",
    },
    {
      id: "jan-3",
      name: "Beloit Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 E Grand Ave",
      city: "Beloit",
      state: "WI",
      zip: "53511",
      phone: "(608) 365-5201",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "17 miles south of Janesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Beloit and Wisconsin-Illinois border residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "jan-4",
      name: "Whitewater Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "213 W Center St",
      city: "Whitewater",
      state: "WI",
      zip: "53190",
      phone: "(262) 473-2711",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "20 miles east of Janesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Whitewater, UW-Whitewater students, and eastern Rock County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "jan-5",
      name: "Milton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "640 S Janesville St",
      city: "Milton",
      state: "WI",
      zip: "53563",
      phone: "(608) 868-2810",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "10 miles north of Janesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Milton and northern Rock County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "jan-6",
      name: "Fort Atkinson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "302 S Water St W",
      city: "Fort Atkinson",
      state: "WI",
      zip: "53538",
      phone: "(920) 563-8526",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "25 miles northeast of Janesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Fort Atkinson and Jefferson County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "105 miles — 1 hr 45 min via I-90, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming Being Near Illinois Helps",
      description: "Janesville and Beloit are close to Illinois, but the Chicago Passport Agency still requires pre-scheduled appointments. Border proximity doesn't mean walk-in access. We handle the appointment and delivery so you don't waste a 3.5-hour round trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Dimensions",
      description: "Passport photos must be exactly 2x2 inches. Some photo locations in Janesville and Beloit cut them slightly off. Always measure your photos before submitting — even 1/8 inch off will cause rejection.",
    },
    {
      icon: "ri-file-list-line",
      title: "Mixing Up Illinois and Wisconsin Requirements",
      description: "Cross-border residents in Beloit sometimes confuse Illinois and Wisconsin acceptance facility procedures. Your application follows Wisconsin rules — make sure you're at a Wisconsin acceptance facility.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Booking Winter Getaways Without a Valid Passport",
      description: "Rock County families booking winter vacations to Mexico or the Caribbean often realize too late that their passports expired. Always check passport validity before booking international travel.",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport Without Both Parents",
      description: "Minors under 16 must have both parents present at the acceptance facility. Janesville and Beloit families often overlook this, especially when one parent works in Illinois during the day.",
    },
    {
      icon: "ri-time-line",
      title: "Not Checking Processing Times for the Season",
      description: "Passport processing times fluctuate seasonally. Summer applications take longest. Janesville families planning spring break or summer trips should apply 8–10 weeks in advance.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Janesville, Beloit, or Whitewater." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to Chicago — about 105 miles via I-90." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Janesville or Rock County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Janesville from the Chicago Passport Agency?",
      a: "Janesville is approximately 105 miles from the Chicago Passport Agency — about 1 hour 45 minutes via I-90 southeast. Janesville's direct I-90 corridor access makes it one of the more convenient Wisconsin locations for courier delivery to Chicago.",
    },
    {
      q: "Is Beloit closer to Chicago than Janesville?",
      a: "Yes, Beloit is about 90 miles from Chicago via I-90 — a bit closer than Janesville. However, both cities are well-served by Fast Passport Center's courier service. Beloit residents enjoy slightly faster turnaround due to the shorter distance.",
    },
    {
      q: "How fast can I get an emergency passport in Janesville?",
      a: "Emergency processing is typically 24–48 hours. Janesville's position on the I-90 corridor — about 105 miles from Chicago — provides reliable courier turnaround for Rock County travelers.",
    },
    {
      q: "Do you serve Beloit and Whitewater residents?",
      a: "Yes. We serve all of Rock County including Beloit, Whitewater, Milton, Edgerton, Evansville, and beyond. Documents ship overnight from any UPS location in Rock County, and we deliver your passport back to your address.",
    },
    {
      q: "Where do Janesville travelers fly internationally?",
      a: "Janesville travelers typically fly internationally through Milwaukee Mitchell (MKE, 75 miles northeast), Madison Dane County Regional (MSN, 40 miles north), Chicago O'Hare (ORD, 100 miles southeast), or Chicago Rockford (RFD, 35 miles south). MKE and ORD offer the most international connections.",
    },
    {
      q: "How do I renew my passport in Janesville?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing.",
    },
    {
      q: "What documents do I need for a first-time passport in Janesville?",
      a: "You'll need Form DS-11, a certified Wisconsin birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Janesville Main Post Office or Rock County Clerk.",
    },
    {
      q: "Does the Rock County Clerk handle passport applications?",
      a: "Yes. The Rock County Clerk at 51 S Main St in downtown Janesville accepts passport applications during regular business hours. This is a convenient alternative to the post office for Rock County residents.",
    },
    {
      q: "Can I use the Rockford IL airport for international travel from Janesville?",
      a: "Chicago Rockford International Airport (RFD) is about 35 miles south of Janesville and offers some international charter and seasonal service. However, most Janesville travelers use MKE, MSN, or ORD for international flights. A valid U.S. passport is required regardless of departure airport.",
    },
    {
      q: "What if I need a passport from Beloit and work in Illinois?",
      a: "Many Beloit residents work across the border in Illinois. Fast Passport Center serves all Rock County residents regardless of where they work. Your passport is valid for international travel from any U.S. airport — your employment location doesn't affect the application process.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Janesville", address: "1701 Milton Ave, Janesville, WI 53545", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Janesville", address: "2519 Kettering St, Janesville, WI 53546", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Walgreens — Beloit", address: "1745 Madison Rd, Beloit, WI 53511", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Janesville", address: "2826 E Milwaukee St, Janesville, WI 53545", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Whitewater", address: "1131 W Main St, Whitewater, WI 53190", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Janesville", address: "2603 Humes Rd, Janesville, WI 53545", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Janesville", address: "2506 Humes Rd, Janesville, WI 53545", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals for Rock County residents.", featured: true },
    { name: "WI DMV — Beloit", address: "1900 Sutler Ave, Beloit, WI 53511", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Convenient for Beloit and southern Rock County residents near the Illinois border.", featured: false },
    { name: "WI DMV — Whitewater", address: "1315 W Main St, Whitewater, WI 53190", hours: "Tue, Thu: 9:00AM–3:00PM", note: "Limited hours — check availability before visiting. Serves Whitewater and eastern Rock County.", featured: false },
  ],
  airports: [
    {
      code: "MSN",
      name: "Dane County Regional Airport",
      distance: "40 miles north of Janesville",
      address: "4000 International Ln, Madison, WI 53704",
      note: "Janesville's closest commercial airport with connections to major hubs via Delta, American, and United. About 45 minutes north via I-90. Passport required for all international connections.",
      flightUrl: "https://www.msnairport.com/",
      flightLabel: "MSN Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "100 miles southeast of Janesville",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub with extensive global connections. About 1 hour 45 minutes via I-90 southeast. Co-located near the Chicago Passport Agency.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "75 miles northeast of Janesville",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Full-service international airport with extensive domestic and international routes. About 1 hour 15 minutes northeast via I-43.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: false,
    },
  ],
};